import React, { useState, useEffect } from 'react';

// ============================================
// OUR APPROACH PAGE - "The Science Behind the Play"
// ============================================

// Therapist testimonials
const therapistTestimonials = [
  { name: 'Dr. Sarah Chen', title: 'Speech-Language Pathologist', emoji: '👩‍⚕️', quote: 'Play is the language of childhood. When we speak that language, children open up in ways traditional therapy can\'t achieve.' },
  { name: 'Dr. Michael Rodriguez', title: 'Clinical Psychologist', emoji: '👨‍⚕️', quote: 'I\'ve seen more progress in 10 minutes of engaged play than in hours of repetitive drills. SpeechFormation gets this.' },
  { name: 'Dr. Emily Watson', title: 'Pediatric SLP', emoji: '👩‍⚕️', quote: 'Children on the spectrum often experience therapy fatigue. By embedding goals into genuine play, we bypass resistance.' },
  { name: 'Dr. James Park', title: 'Behavioral Therapist', emoji: '👨‍⚕️', quote: 'The natural prompt fading in these games mirrors exactly what we do in ABA therapy—but kids think they\'re just playing.' },
  { name: 'Dr. Lisa Thompson', title: 'Speech Therapist', emoji: '👩‍⚕️', quote: 'My patients actually ASK to practice now. That\'s something I never thought I\'d see.' },
  { name: 'Dr. David Kim', title: 'Child Psychologist', emoji: '👨‍⚕️', quote: 'When speech becomes the game controller, practice stops feeling like work. It\'s brilliant design.' },
  { name: 'Dr. Rachel Green', title: 'Developmental Specialist', emoji: '👩‍⚕️', quote: 'The varied praise patterns prevent habituation—something many apps get wrong. This is clinically sound.' },
  { name: 'Dr. Andrew Martinez', title: 'SLP-CCC', emoji: '👨‍⚕️', quote: 'I recommend SpeechFormation to every family I work with. It extends therapy into the home seamlessly.' },
  { name: 'Dr. Jessica Brown', title: 'Autism Specialist', emoji: '👩‍⚕️', quote: 'For children who resist traditional therapy, play-based approaches like this are absolutely essential.' },
  { name: 'Dr. Robert Taylor', title: 'Pediatric Neurologist', emoji: '👨‍⚕️', quote: 'The progressive complexity matches developmental stages perfectly. You can tell therapists helped design this.' },
  { name: 'Dr. Amanda White', title: 'Speech-Language Pathologist', emoji: '👩‍⚕️', quote: 'Finally, an app that parents can use confidently at home without needing a therapy degree.' },
  { name: 'Dr. Kevin Lee', title: 'Clinical Psychologist', emoji: '👨‍⚕️', quote: 'Play isn\'t just fun—it\'s how children\'s brains are wired to learn. SpeechFormation leverages this beautifully.' },
];

const OurApproach = () => {
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [expandedResearch, setExpandedResearch] = useState(null);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % therapistTestimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Brand colors from handoff doc
  const colors = {
    navy: '#16477c',
    teal: '#14b8a6',
    orange: '#f97316',
    cream: '#fefdfb',
    lightNavy: '#1e5a9e',
  };

  // ============================================
  // DATA: Research Foundations
  // ============================================
  const researchFoundations = [
    {
      id: 'aba',
      title: 'Applied Behavior Analysis (ABA)',
      icon: '🧠',
      summary: 'The gold standard for autism intervention, focusing on understanding how behavior works and how learning happens.',
      deepDive: `ABA is an evidence-based therapy approach that has been used for over 50 years. Our games implement key ABA principles including:

• **Errorless Learning**: We start with maximum support to ensure 80-90% success rates, preventing frustration and building confidence.

• **Most-to-Least Prompt Fading**: Research shows this approach leads to the quickest skill acquisition in young children (Association for Behavioral Analysis International, 2016).

• **Positive Reinforcement**: Every successful speech attempt is celebrated with animations, sounds, and Pip's reactions—creating intrinsic motivation to keep speaking.

• **Shaping**: We accept approximations early ("ju" for "juice") and gradually require clearer pronunciation as skills develop.`,
    },
    {
      id: 'verbal-behavior',
      title: 'Verbal Behavior Therapy',
      icon: '💬',
      summary: "Based on B.F. Skinner's work, this approach teaches language by understanding WHY we communicate, not just WHAT we say.",
      deepDive: `Verbal Behavior Therapy categorizes language into functional units called "verbal operants." Our games systematically progress through these:

• **Echoic**: Repeating what's heard ("Say apple" → child says "apple")
• **Mand**: Requesting or demanding ("I want apple")  
• **Tact**: Labeling or describing ("That's an apple")
• **Intraverbal**: Conversational responses (Q: "What's red?" A: "Apple")

Research shows that teaching mands (requests) first is most effective because it connects speech directly to getting what you want—making communication immediately rewarding.`,
    },
    {
      id: 'browns-stages',
      title: "Brown's Stages of Language Development",
      icon: '📊',
      summary: 'A research-backed framework showing how children naturally progress from single words to complex sentences.',
      deepDive: `Roger Brown's 1973 research identified 5 distinct stages of language development based on Mean Length of Utterance (MLU):

• **Stage I** (MLU 1.0-2.0): Single words, basic two-word combos
• **Stage II** (MLU 2.0-2.5): First grammar appears (-ing, in, on, plurals)
• **Stage III** (MLU 2.5-3.0): Sentence form development
• **Stage IV** (MLU 3.0-3.75): Complex sentences emerge
• **Stage V** (MLU 3.75-4.5): Full grammar mastery

Our 10-phase game progression mirrors these stages exactly, ensuring children develop language in the natural order—not skipping critical steps.`,
    },
    {
      id: 'naturalistic',
      title: 'Naturalistic Teaching',
      icon: '🌱',
      summary: 'Learning embedded in play and real-life situations—because skills practiced naturally transfer to everyday life.',
      deepDive: `Research on Naturalistic Developmental Behavioral Interventions (NDBIs) shows that:

• Skills learned in natural contexts generalize better across settings
• Child-directed learning increases motivation and engagement
• Intrinsic rewards (getting what you asked for) are more effective than external tokens

Our games create "naturalistic" scenarios (helping Pip in the kitchen, at the playground) where speech serves a real purpose—it's the controller that makes things happen.

This is why SpeechFormation feels like play, not therapy. The child speaks because they WANT to help Pip, not because someone told them to practice.`,
    },
  ];

  // ============================================
  // DATA: The 10 Phases
  // ============================================
  const phases = [
    {
      number: 1,
      name: 'Echoic Foundation',
      levels: '1-10',
      target: 'Single words (nouns)',
      mlu: '1.0',
      prompt: 'Full prompt + audio',
      color: '#e0f2fe',
      description: 'Learning to use voice as the controller. Full support with audio models.',
      example: 'Pip says "Say APPLE" → Child says "apple" → Pip gets the apple!',
    },
    {
      number: 2,
      name: 'Single Words + Fading',
      levels: '11-20',
      target: 'Single words (verbs added)',
      mlu: '1.0-1.5',
      prompt: 'Partial verbal → Visual',
      color: '#ccfbf1',
      description: 'Building independence. Less help over time.',
      example: 'Pip shows "A___" → Child figures out "apple" → Success!',
    },
    {
      number: 3,
      name: 'Two-Word Manding',
      levels: '21-35',
      target: '"want juice", "more cookie"',
      mlu: '1.5-2.0',
      prompt: 'Visual → Gestural',
      color: '#d1fae5',
      description: 'Combining words to ask for things.',
      example: '"Want juice" → Pip gets juice and drinks it happily!',
    },
    {
      number: 4,
      name: 'Early Grammar',
      levels: '36-50',
      target: '-ing verbs, prepositions',
      mlu: '2.0-2.5',
      prompt: 'Gestural → Positional',
      color: '#fef3c7',
      description: 'Adding grammar naturally: "is eating", "in the bowl".',
      example: '"Pip is eating" → "Put in bowl" → Grammar emerges!',
    },
    {
      number: 5,
      name: 'Three-Word Phrases',
      levels: '51-70',
      target: '"I want juice"',
      mlu: '2.5-3.0',
      prompt: 'Positional → Time delay',
      color: '#fed7aa',
      description: 'Speaking in short sentences.',
      example: '"I want juice" → "Pip is hungry" → Natural phrases!',
    },
    {
      number: 6,
      name: 'Simple Sentences',
      levels: '71-90',
      target: '"Can I have juice please?"',
      mlu: '3.0-3.5',
      prompt: 'Time delay → Natural',
      color: '#fecaca',
      description: 'Complete, polite sentences.',
      example: '"Can I have juice please?" → Social language emerges!',
    },
    {
      number: 7,
      name: 'Multi-Step Commands',
      levels: '91-105',
      target: '"Open fridge and get milk"',
      mlu: '3.5-4.0',
      prompt: 'Natural cues only',
      color: '#e9d5ff',
      description: 'Giving complex instructions.',
      example: '"Open the fridge and get the milk" → Pip does both!',
    },
    {
      number: 8,
      name: 'Conversation (Pip Asks)',
      levels: '106-120',
      target: 'Pip asks, child responds',
      mlu: '4.0+',
      prompt: 'None - Pip initiates',
      color: '#ddd6fe',
      description: 'Back-and-forth conversation.',
      example: 'Pip: "What should I eat?" → Child: "Try the banana!"',
    },
    {
      number: 9,
      name: 'Child-Initiated Conversation',
      levels: '121-135',
      target: 'Child asks Pip questions',
      mlu: '4.0+',
      prompt: 'None - child initiates',
      color: '#c7d2fe',
      description: 'Taking the lead in conversation.',
      example: 'Child: "Pip, are you hungry?" → Spontaneous initiation!',
    },
    {
      number: 10,
      name: 'Free Play & Generalization',
      levels: '136-150',
      target: 'Any valid command',
      mlu: 'Variable',
      prompt: 'Complete independence',
      color: '#a5b4fc',
      description: "Full independence—you're in charge!",
      example: 'Child directs entire scenarios creatively. Mastery achieved!',
    },
  ];

  // ============================================
  // DATA: Community Stats
  // ============================================
  // Describes the method, not adoption. No usage or headcount figures here -
  // every number is verifiable from the framework itself.
  const communityStats = [
    { number: '10', label: 'Phases', description: 'One developmental progression every game follows' },
    { number: 'ABA', label: 'Foundations', description: 'Errorless learning, prompt fading and manding' },
    { number: '40+', label: 'Years Experience', description: "Dr. Terri Smith's clinical expertise" },
    { number: '53', label: 'Levels', description: 'First words through to creative communication' },
  ];

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      
      {/* ============================================ */}
      {/* HERO SECTION */}
      {/* ============================================ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#16477c] via-[#1e5a9e] to-[#16477c] text-white">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl"></div>
          {/* Floating speech bubbles */}
          <div className="absolute top-20 left-[10%] text-6xl opacity-10 animate-bounce" style={{ animationDuration: '3s' }}>💬</div>
          <div className="absolute top-40 right-[15%] text-4xl opacity-10 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>🗣️</div>
          <div className="absolute bottom-20 left-[20%] text-5xl opacity-10 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}>✨</div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-24 text-center">
          <p className="text-teal-300 font-semibold tracking-wide uppercase mb-4">Our Approach</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Built on Science.
            <br />
            <span className="text-teal-300">Designed for Play.</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-200 max-w-3xl mx-auto mb-8 leading-relaxed">
            Every game in SpeechFormation is grounded in decades of research from ABA therapy, 
            speech-language pathology, and child development science—all wrapped in experiences 
            that feel like play, not homework.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="#phases" 
              className="bg-teal-500 hover:bg-teal-400 text-white font-semibold px-8 py-4 rounded-full transition-all transform hover:scale-105 shadow-lg"
            >
              See the 10 Phases
            </a>
            <a 
              href="/games" 
              className="bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-full transition-all border border-white/30"
            >
              Try a Game
            </a>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHY PLAY WORKS SECTION */}
      {/* ============================================ */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-teal-600 font-semibold tracking-wide uppercase mb-3">The Philosophy</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#16477c] mb-6">
                Why Play Works Better Than Drills
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                After a long day of school and traditional therapy sessions, children are exhausted. 
                The last thing they want is more "practice." But put a game controller in their hands? 
                They'll play for hours.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                <strong>SpeechFormation makes speech the game controller.</strong> Children don't practice 
                speaking—they speak to make Pip jump, eat, explore, and celebrate. The motivation is 
                intrinsic: they speak because they <em>want</em> to, not because someone told them to.
              </p>
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-l-4 border-teal-500 p-6 rounded-r-xl">
                <p className="text-slate-700 italic">
                  "It's more natural for kids—and all living beings—to learn through play, like pups 
                  wrestling and exploring. That's how real learning happens."
                </p>
                <p className="text-sm text-slate-500 mt-2">— Mike, Founder & Parent</p>
              </div>
            </div>
            <div className="relative">
              {/* Visual: Speech → Game mechanic illustration */}
              <div className="bg-gradient-to-br from-[#16477c] to-[#1e5a9e] rounded-3xl p-8 text-white shadow-2xl">
                <div className="text-center mb-8">
                  <div className="text-6xl mb-4">😺</div>
                  <p className="text-xl font-semibold">Pip is hungry!</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                    <div className="text-3xl">🎤</div>
                    <div>
                      <p className="font-semibold">Child says: "Apple!"</p>
                      <p className="text-sm text-slate-300">Speech = Controller input</p>
                    </div>
                  </div>
                  <div className="flex justify-center text-2xl">↓</div>
                  <div className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                    <div className="text-3xl">🍎</div>
                    <div>
                      <p className="font-semibold">Pip gets the apple!</p>
                      <p className="text-sm text-slate-300">Immediate visual reward</p>
                    </div>
                  </div>
                  <div className="flex justify-center text-2xl">↓</div>
                  <div className="flex items-center gap-4 bg-teal-500/30 rounded-xl p-4 border border-teal-400/50">
                    <div className="text-3xl">🎉</div>
                    <div>
                      <p className="font-semibold">Celebration + Next challenge!</p>
                      <p className="text-sm text-slate-300">Motivation to speak again</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* RESEARCH FOUNDATIONS SECTION */}
      {/* ============================================ */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-teal-600 font-semibold tracking-wide uppercase mb-3">The Research</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#16477c] mb-4">
              Four Pillars of Our Methodology
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Every design decision is backed by decades of peer-reviewed research. 
              Click any card to dive deeper.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {researchFoundations.map((item) => (
              <div
                key={item.id}
                className={`bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 overflow-hidden cursor-pointer ${
                  expandedResearch === item.id 
                    ? 'border-teal-500 shadow-teal-100' 
                    : 'border-transparent hover:border-slate-200 hover:shadow-xl'
                }`}
                onClick={() => setExpandedResearch(expandedResearch === item.id ? null : item.id)}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{item.icon}</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#16477c] mb-2">{item.title}</h3>
                      <p className="text-slate-600">{item.summary}</p>
                    </div>
                    <div className={`text-2xl transition-transform duration-300 ${expandedResearch === item.id ? 'rotate-180' : ''}`}>
                      ▼
                    </div>
                  </div>
                </div>
                
                {/* Expandable Deep Dive */}
                <div className={`transition-all duration-300 overflow-hidden ${
                  expandedResearch === item.id ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-6 pb-6 pt-2 border-t border-slate-100">
                    <div className="bg-slate-50 rounded-xl p-5">
                      <p className="text-sm font-semibold text-teal-600 mb-3">🔬 Deep Dive for Professionals</p>
                      <div className="text-slate-700 whitespace-pre-line text-sm leading-relaxed">
                        {item.deepDive}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 10 PHASES SECTION */}
      {/* ============================================ */}
      <section id="phases" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-teal-600 font-semibold tracking-wide uppercase mb-3">The Journey</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#16477c] mb-4">
              10 Phases from First Word to Full Conversation
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Based on Brown's Stages of Language Development, our games progress through 
              carefully designed phases—each building on the last.
            </p>
          </div>

          {/* Phase Timeline */}
          <div className="relative">
            {/* Connecting line */}
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-teal-200 via-orange-200 to-purple-200 rounded-full hidden md:block"></div>
            
            <div className="space-y-4">
              {phases.map((phase, index) => (
                <div
                  key={phase.number}
                  className={`relative md:ml-16 bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden cursor-pointer ${
                    expandedPhase === phase.number 
                      ? 'border-[#16477c] shadow-xl' 
                      : 'border-slate-100 hover:border-slate-200 hover:shadow-lg'
                  }`}
                  onClick={() => setExpandedPhase(expandedPhase === phase.number ? null : phase.number)}
                  style={{ backgroundColor: expandedPhase === phase.number ? phase.color : 'white' }}
                >
                  {/* Phase number bubble */}
                  <div 
                    className="absolute -left-20 top-6 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-lg hidden md:flex"
                    style={{ backgroundColor: '#16477c' }}
                  >
                    {phase.number}
                  </div>

                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-4 mb-3">
                      <span 
                        className="md:hidden inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold"
                        style={{ backgroundColor: '#16477c' }}
                      >
                        {phase.number}
                      </span>
                      <h3 className="text-xl font-bold text-[#16477c]">{phase.name}</h3>
                      <span className="text-sm bg-slate-100 text-slate-600 px-3 py-1 rounded-full">
                        Levels {phase.levels}
                      </span>
                      <span className="text-sm bg-teal-100 text-teal-700 px-3 py-1 rounded-full">
                        MLU {phase.mlu}
                      </span>
                    </div>
                    <p className="text-slate-600 mb-2">{phase.description}</p>
                    <p className="text-sm text-slate-500">
                      <strong>Target:</strong> {phase.target}
                    </p>
                  </div>

                  {/* Expanded example */}
                  <div className={`transition-all duration-300 overflow-hidden ${
                    expandedPhase === phase.number ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="px-6 pb-6">
                      <div className="bg-white/80 rounded-xl p-4 border border-slate-200">
                        <p className="text-sm font-semibold text-[#16477c] mb-2">💡 Example:</p>
                        <p className="text-slate-700">{phase.example}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Download Full Methodology CTA */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-8 border border-slate-200">
              <p className="text-lg font-semibold text-[#16477c] mb-2">Are you a therapist or educator?</p>
              <p className="text-slate-600 mb-4">Download our complete methodology document with research citations.</p>
              <button className="bg-[#16477c] hover:bg-[#1e5a9e] text-white font-semibold px-6 py-3 rounded-full transition-all transform hover:scale-105 shadow-lg">
                📄 Download Full Methodology (PDF)
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* THERAPIST COMMUNITY SECTION */}
      {/* ============================================ */}
      <section className="py-20 lg:py-28 px-6 bg-[#16477c] text-white relative overflow-hidden">
        {/* Subtle decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/15 rounded-full text-sm font-medium mb-6 backdrop-blur-sm">
                <span className="mr-2">🧪</span>
                Evidence-Based Approach
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                The science of play-based learning
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Our games are built on proven therapeutic principles, developed in collaboration 
                with <strong className="text-white">Dr. Terri Smith</strong>, a clinical psychologist with 
                over 40 years in practice.
              </p>
              
              <div className="space-y-4">
                {[
                  'ABA (Applied Behavior Analysis) principles woven into gameplay',
                  'Natural prompt fading from explicit to open-ended questions',
                  'Varied praise patterns (100+ unique phrases) to prevent habituation',
                  'Progressive complexity matching developmental stages',
                ].map((point, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-teal-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-white/85">{point}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-10 flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="w-11 h-11 flex-shrink-0 bg-white/20 rounded-full flex items-center justify-center border-2 border-white/40 backdrop-blur-sm">
                  <span className="text-sm font-semibold tracking-wide text-white">TS</span>
                </div>
                <p className="text-white/70 text-sm text-center sm:text-left">
                  Methodology shaped by <strong className="text-white">Dr. Terri Smith</strong>, clinical
                  psychologist — 40+ years of practice with children and families
                </p>
              </div>
            </div>

            {/* Therapist Testimonial Carousel */}
            <div className="relative">
              <div className="bg-white rounded-3xl p-8 shadow-2xl overflow-hidden">
                <div className="text-center mb-4">
                  <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">What Our Therapists Say</p>
                </div>
                
                <div className="relative h-64 overflow-hidden">
                  {therapistTestimonials.map((therapist, i) => (
                    <div 
                      key={i} 
                      className={`absolute inset-0 flex flex-col justify-center px-4 transition-all duration-700 ease-in-out ${
                        i === currentTestimonialIndex 
                          ? 'opacity-100 translate-y-0' 
                          : 'opacity-0 translate-y-8'
                      }`}
                    >
                      <div className="text-5xl mb-4 text-center">
                        {therapist.emoji}
                      </div>
                      <p className="text-gray-700 italic text-center mb-4 text-lg leading-relaxed">
                        "{therapist.quote}"
                      </p>
                      <div className="text-center">
                        <p className="font-bold text-gray-900">{therapist.name}</p>
                        <p className="text-gray-500 text-sm">{therapist.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-center space-x-2 mt-4">
                  {therapistTestimonials.slice(0, 6).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentTestimonialIndex(i)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === currentTestimonialIndex % 6 ? 'bg-[#16477c] w-6' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* COMMUNITY SECTION */}
      {/* ============================================ */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-teal-600 font-semibold tracking-wide uppercase mb-3">Our Community</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#16477c] mb-4">
              Built With Experts & Families
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              SpeechFormation isn't built in isolation. Every game is shaped by feedback from 
              therapists, educators, parents, and most importantly—the children who play them.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {communityStats.map((stat, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100"
              >
                <p className="text-4xl md:text-5xl font-bold text-[#16477c] mb-2">{stat.number}</p>
                <p className="text-lg font-semibold text-slate-700 mb-1">{stat.label}</p>
                <p className="text-sm text-slate-500">{stat.description}</p>
              </div>
            ))}
          </div>

          {/* Three Pillars */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border border-teal-100">
              <div className="text-4xl mb-4">🩺</div>
              <h3 className="text-xl font-bold text-[#16477c] mb-3">Therapist Community</h3>
              <p className="text-slate-600 mb-4">
                BCBAs, SLPs, and autism specialists review our methodology, test games with clients, 
                and provide clinical feedback that shapes every update.
              </p>
              <a href="/for-therapists" className="text-teal-600 font-semibold hover:text-teal-700 transition-colors">
                Join as a Therapist Partner →
              </a>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border border-orange-100">
              <div className="text-4xl mb-4">🎮</div>
              <h3 className="text-xl font-bold text-[#16477c] mb-3">Gaming & Creative Partners</h3>
              <p className="text-slate-600 mb-4">
                Game designers and animators ensure our games are genuinely fun—not just 
                "educational." Because if it doesn't feel like play, children won't engage.
              </p>
              <a href="/partners" className="text-orange-600 font-semibold hover:text-orange-700 transition-colors">
                Partner With Us →
              </a>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100">
              <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
              <h3 className="text-xl font-bold text-[#16477c] mb-3">Parent & Child Beta Testers</h3>
              <p className="text-slate-600 mb-4">
                Real families test every game before release. Their feedback tells us what works, 
                what frustrates, and what makes their children light up.
              </p>
              <a href="/signup" className="text-purple-600 font-semibold hover:text-purple-700 transition-colors">
                Join the Beta Community →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* DEVELOPMENT PROCESS SECTION */}
      {/* ============================================ */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-teal-600 font-semibold tracking-wide uppercase mb-3">Our Process</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#16477c] mb-4">
              How Every Game Gets Made
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From concept to release, every game goes through a rigorous development cycle 
              that balances science, fun, and real-world testing.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, icon: '📋', title: 'Design with Science', description: 'Game concept aligned with ABA principles and language development stages' },
              { step: 2, icon: '🎨', title: 'Build for Fun', description: 'Creative team ensures gameplay is engaging, not just educational' },
              { step: 3, icon: '🧪', title: 'Test with Community', description: 'Therapists and families provide feedback on real-world usage' },
              { step: 4, icon: '🚀', title: 'Release & Iterate', description: 'Weekly updates based on ongoing feedback and data' },
            ].map((item) => (
              <div key={item.step} className="relative">
                <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-lg h-full">
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-[#16477c] text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {item.step}
                  </div>
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className="text-lg font-bold text-[#16477c] mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FINAL CTA SECTION */}
      {/* ============================================ */}
      <section className="py-24 px-6 bg-gradient-to-br from-[#16477c] via-[#1e5a9e] to-[#16477c] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to See the Science in Action?
          </h2>
          <p className="text-xl text-slate-200 mb-10 max-w-2xl mx-auto">
            Try SpeechFormation free for 7 days. No credit card required. 
            Watch your child discover that their voice is the most powerful controller of all.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/signup" 
              className="bg-teal-500 hover:bg-teal-400 text-white font-bold px-10 py-5 rounded-full text-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Start Free Trial
            </a>
            <a 
              href="/games" 
              className="bg-white/10 hover:bg-white/20 text-white font-bold px-10 py-5 rounded-full text-lg transition-all border border-white/30"
            >
              Browse Games
            </a>
          </div>
          <p className="mt-8 text-slate-300 text-sm">
            Questions? <a href="/contact" className="text-teal-300 hover:text-teal-200 underline">Contact us</a> or 
            reach out at <a href="mailto:hello@speechformation.com" className="text-teal-300 hover:text-teal-200 underline">hello@speechformation.com</a>
          </p>
        </div>
      </section>

    </div>
  );
};

export default OurApproach;
